import React, { useState, useEffect } from 'react';
import { Construction, Save, Clock, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminMaintenance = () => {
  const [settings, setSettings] = useState({
    is_enabled: false,
    message: "We're currently performing maintenance. Please check back soon!",
    expected_end_time: '',
    allow_admin_access: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/maintenance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/maintenance`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast({
          title: settings.is_enabled ? "Maintenance Mode Enabled" : "Maintenance Mode Disabled",
          description: settings.is_enabled ? "Users will see the maintenance page" : "Site is now accessible to all users"
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Maintenance Mode</h1>
        <p className="text-gray-600">Temporarily take your site offline for maintenance</p>
      </div>

      {/* Status Banner */}
      <Card className={settings.is_enabled ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {settings.is_enabled ? (
              <>
                <Construction className="w-8 h-8 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Maintenance Mode is ON</p>
                  <p className="text-sm text-red-700">Users cannot access the site (except admins if allowed)</p>
                </div>
              </>
            ) : (
              <>
                <Shield className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Site is Live</p>
                  <p className="text-sm text-green-700">All users can access the site normally</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Maintenance Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="font-semibold text-lg">Enable Maintenance Mode</Label>
              <p className="text-sm text-gray-500">Turn on to show maintenance page to users</p>
            </div>
            <Switch
              checked={settings.is_enabled}
              onCheckedChange={(c) => setSettings(p => ({ ...p, is_enabled: c }))}
            />
          </div>

          {/* Warning */}
          {settings.is_enabled && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Warning</p>
                <p className="text-sm text-amber-700">
                  Users will not be able to login, register, or use any features while maintenance mode is enabled.
                </p>
              </div>
            </div>
          )}

          {/* Message */}
          <div className="space-y-2">
            <Label>Maintenance Message</Label>
            <textarea
              value={settings.message}
              onChange={(e) => setSettings(p => ({ ...p, message: e.target.value }))}
              placeholder="Message to show users..."
              className="w-full h-24 p-3 border rounded-lg resize-none"
            />
          </div>

          {/* Expected End Time */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> Expected End Time (Optional)
            </Label>
            <Input
              type="datetime-local"
              value={settings.expected_end_time}
              onChange={(e) => setSettings(p => ({ ...p, expected_end_time: e.target.value }))}
            />
            <p className="text-xs text-gray-500">Users will see when the site is expected to be back</p>
          </div>

          {/* Allow Admin Access */}
          <div className="flex items-center gap-3 p-4 border rounded-lg">
            <Switch
              checked={settings.allow_admin_access}
              onCheckedChange={(c) => setSettings(p => ({ ...p, allow_admin_access: c }))}
            />
            <div>
              <Label>Allow Admin Access</Label>
              <p className="text-xs text-gray-500">Admins can still access the site during maintenance</p>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="border rounded-lg p-8 bg-gray-100 text-center">
              <Construction className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Under Maintenance</h2>
              <p className="text-gray-600 mb-4">{settings.message}</p>
              {settings.expected_end_time && (
                <p className="text-sm text-gray-500">
                  Expected back: {new Date(settings.expected_end_time).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default AdminMaintenance;
